<template>
    <div>
        <div v-if="currentUserIsOrganizationAdmin && currentUserOrganizationUnitId == organizationUnitId" id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 v-if="organizationUnitId" :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Manage user's roles</h1>

                    <button @click="$router.go(-1)" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        <span>Back</span>
                    </button>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="id && user" class="container">
            <h4 class="fg-color-blue">{{displayName}} is currently {{ roles.join(', ') }}</h4>
            <br />
            <div class="row">
                <div v-if="currentUserIsSystemAdmin || currentUserIsGlobalAdmin" class="col-sm-4">
                    <div class="card text-white" :class="[isGlobalAdmin ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Global Admin</div>
                        <div class="card-body">
                            <p class="card-text" style="height: 200px;">
                                Users in this role have an access to admin panel and can set up organizations. They permissions include:
                                <ul>
                                    <li>Setting up a new organizations</li>
                                    <li>Assign global admins</li>
                                    <li>Assign organization admins</li>
                                </ul>
                            </p>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isGlobalAdmin" @click="handleAssignGlobalAdminRoleCommandV1" type="button" class="btn btn-light">Assign Global Admin</button>
                            <button v-else @click="handleRevokeGlobalAdminRoleCommandV1" type="button" class="btn btn-light">Revoke Global Admin</button>
                        </div>
                    </div>
                </div>
                <div v-if="currentUserIsSystemAdmin || currentUserIsGlobalAdmin || currentUserIsOrganizationAdmin" class="col-sm-4">
                    <div class="card text-white" :class="[isOrganizationAdmin ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Organization Admin</div>
                        <div class="card-body">
                            <p class="card-text" style="height: 200px;">
                                Users in this role have an access to admin panel and can manage within a single organizations. They permissions include:
                                <ul>
                                    <li>Managing users</li>
                                    <li>Managing workspaces</li>
                                    <li>Importing portfolios</li>
                                </ul>
                            </p>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isOrganizationAdmin" @click="handleAssignOrganizationAdminRoleCommandV1" type="button" class="btn btn-light">Assign Organization Admin</button>
                            <button v-else @click="handleRevokeOrganizationAdminRoleCommandV1" type="button" class="btn btn-light">Revoke Organization Admin</button>
                        </div>
                    </div>
                </div>
                <div v-if="currentUserIsSystemAdmin || currentUserIsOrganizationAdmin" class="col-sm-4">
                    <div class="card text-white" :class="[isWorkspaceUser ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Workspace User</div>
                        <div class="card-body">
                            <p class="card-text" style="height: 200px;">
                                Users in this role have an access to workspaces and its portfolios. They permissions include:
                                <ul>
                                    <li>Creating a new portfolios</li>
                                    <li>Managing projects</li>
                                    <li>Managing criteria</li>
                                    <li>Managing surveys</li>
                                    <li>Browsing results</li>
                                </ul>
                            </p>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isWorkspaceUser" @click="handleAssignWorkspaceUserRoleCommandV1" type="button" class="btn btn-light">Assign Workspace User</button>
                            <button v-else @click="handleRevokeWorkspaceUserRoleCommandV1" type="button" class="btn btn-light">Revoke Workspace User</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="!id" class="container">
            <div class="input-group mb-3">
                <input v-model="emailinput" type="email" class="form-control" placeholder="Find user by email">
                <div class="input-group-append">
                    <button @click="handleFindUser" class="btn btn-outline-secondary" type="button">Find</button>
                </div>
            </div>
        </div>

        <div v-else class="container">
            <h4>Email '{{id}}' not found</h4>
        </div>
    </div>
</template>

<script lang="ts">
    import Component, { mixins } from 'vue-class-component';
    import PageBase from "@common/PageBase";
    import Queries from "@Organizations/Users/Domain/queries-vue-component";
    import Commands from "@Organizations/Users/Domain/commands-vue-component";
    import "@common/AdminNavigationV2.vue";

    @Component
    export default class ManageUserRolesPage extends mixins(PageBase, Queries, Commands) {
        mounted() {
            this.setTitle("Manage user's roles");
            this.runCurrentUserQueryV1(false, false, true);
            this.runUserQueryV1(this.id);
        }

        get userSessionDetails() { return this.model?.UserSessionDetails; }
        get userWithSensitiveData() { return this.model?.UserWithSensitiveData; }

        get currentUserOrganizationUnitId() { return this.userSessionDetails?.OrganizationUnitId; }
        get currentUserIsSystemAdmin() { return this.userSessionDetails?.IsSystemAdmin || false; }
        get currentUserIsGlobalAdmin() { return this.userSessionDetails?.IsGlobalAdmin || false; }
        get currentUserIsOrganizationAdmin() { return this.userSessionDetails?.IsOrganizationAdmin || false; }

        get user() { return this.userWithSensitiveData; }
        get email() { return this.user?.Email; }
        get displayName() { return this.user?.DisplayName; }
        get isSystemAdmin() { return this.user?.IsSystemAdmin; }
        get isGlobalAdmin() { return this.user?.IsGlobalAdmin; }
        get isOrganizationAdmin() { return this.user?.IsOrganizationAdmin; }
        get isWorkspaceUser() { return this.user?.IsWorkspaceUser; }
        get organizationUnitId() { return this.user?.OrganizationUnitId; }

        get roles() {
            const roles = [];
            if (this.isSystemAdmin) roles.push('System Admin');
            if (this.isGlobalAdmin) roles.push('Global Admin');
            if (this.isOrganizationAdmin) roles.push('Organization Admin');
            if (this.isWorkspaceUser) roles.push('Workspace User');
            return roles;
        }

        succeedAnyCommand() {
            this.runUserQueryV1(this.id);
        }

        failedAnyCommand(status) {
            if (status.Auth === false) this.navigateTo('/#/signin');
        }

        handleAssignGlobalAdminRoleCommandV1() {
            if (confirm(`Do you really want to assign Global Admin role for ${this.email}?`))
                this.invokeAssignGlobalAdminRoleCommandV1(this.id);
        }

        handleRevokeGlobalAdminRoleCommandV1() {
            if (confirm(`Do you really want to revoke Global Admin role for ${this.email}`))
                this.invokeRevokeGlobalAdminRoleCommandV1(this.id);
        }

        handleAssignOrganizationAdminRoleCommandV1() {
            if (confirm(`Do you really want to assign Organization Admin role for ${this.email}?`))
                this.invokeAssignOrganizationAdminRoleCommandV1(this.id);
        }

        handleRevokeOrganizationAdminRoleCommandV1() {
            if (confirm(`Do you really want to revoke Organization Admin role for ${this.email}`))
                this.invokeRevokeOrganizationAdminRoleCommandV1(this.id);
        }

        handleAssignWorkspaceUserRoleCommandV1() {
            if (confirm(`Do you really want to assign Workspace User role for ${this.email}?`))
                this.invokeAssignWorkspaceUserRoleCommandV1(this.id);
        }

        handleRevokeWorkspaceUserRoleCommandV1() {
            if (confirm(`Do you really want to revoke Workspace User role for ${this.email}`))
                this.invokeRevokeWorkspaceUserRoleCommandV1(this.id);
        }

        handleFindUser() {
            this.navigateTo(`/#/admin/organizations/users/manage/roles/${this.emailinput}`);
        }
    }
</script>

<style scoped>
</style>